import type { PsHttpSSE } from '@patsnap-utils/sse';

/**
 * Claim Chart 比对请求参数
 */
export interface ClaimChartRequest {
    /**
     * 任务 ID
     */
    task_id: string;
    /**
     * 比对阶段
     */
    stage?: 'blue' | 'red' | 'judge';
}

/**
 * Claim 映射项
 */
export interface ClaimMapping {
    /**
     * Claim 文本
     */
    claim: string;
    /**
     * TS 章节
     */
    ts_section: string[];
    /**
     * TS 内容
     */
    ts_content: string[];
    /**
     * 映射原因
     */
    reason: string[];
    /**
     * 实体映射
     */
    entity_mapping?: Record<string, string>;
}

/**
 * Claim Chart 结果
 */
export interface ClaimChartResult {
    /**
     * 是否为 SEP
     */
    is_sep: string;
    /**
     * 解释说明
     */
    explanation: string;
    /**
     * 映射列表
     */
    mappings: ClaimMapping[];
}

/**
 * 比对结果项
 */
export interface CompareResultItem {
    /** Claim 编号（多 Claim 结果使用） */
    claim_number?: number;
    /** 子任务 ID（多 Claim 结果使用） */
    child_task_id?: string;
    /** 当前 Claim 的阶段状态 */
    claim_status?: string;
    /**
     * 标准 ID
     */
    std_id: string;
    /**
     * 是否成功
     */
    success: boolean;
    /**
     * 进程 ID
     */
    pid: string;
    /**
     * TS 编号
     */
    ts_code: string;
    /**
     * 版本
     */
    version: string;
    /**
     * Claim 文本
     */
    claim: string;
    /**
     * Claim Chart 结果
     */
    cc_result: ClaimChartResult;
    /**
     * 原始结果（JSON 字符串）
     */
    raw_cc_result: string;
}

/**
 * Claim Chart 比对响应
 */
export interface ClaimChartResponse {
    /**
     * 请求状态
     */
    status: boolean;
    /**
     * 错误代码
     */
    error_code: number;
    /**
     * 错误消息
     */
    error_msg?: string;
    /**
     * 响应数据
     */
    data?: {
        /**
         * 比对结果列表
         */
        compare_results: CompareResultItem[];
        /** 多 Claim 分组信息，供页面切换并保留失败项 */
        claim_results?: Array<{
            claim_number: number;
            child_task_id?: string;
            claim_status?: string;
            success: boolean;
            compare_results: CompareResultItem[];
        }>;
    };
}

export const ClaimChartApi = Symbol('ClaimChartApi');

/**
 * Claim Chart API
 */
export interface ClaimChartApi {
    /**
     * 获取 Claim Chart 比对结果（兼容旧调用，内部使用 SSE）
     */
    getClaimChart(request: ClaimChartRequest): Promise<ClaimChartResponse>;

    /**
     * 获取 Claim Chart 比对结果流（SSE）
     */
    getClaimChartStream(request: ClaimChartRequest): Promise<PsHttpSSE<ClaimChartResponse>>;
}
